import { useState, useEffect, useCallback } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import {
  Calendar, RefreshCw, TrendingUp, TrendingDown, Minus,
  AlertTriangle, Clock, Globe, ChevronLeft, ChevronRight
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CURRENCIES = ["All", "USD", "EUR", "GBP", "JPY", "AUD", "CAD", "CHF", "NZD"];

const IMPACT_CONFIG = {
  high:   { label: "High",   color: "text-[#FF3D00]", bg: "bg-[#FF3D00]/10 border-[#FF3D00]/30", dot: "bg-[#FF3D00]" },
  medium: { label: "Medium", color: "text-[#FFC107]", bg: "bg-[#FFC107]/10 border-[#FFC107]/30", dot: "bg-[#FFC107]" },
  low:    { label: "Low",    color: "text-[#A1A1AA]", bg: "bg-[#27272A]/30 border-[#27272A]",    dot: "bg-[#A1A1AA]" },
};

const OUTCOME_CONFIG = {
  better:  { Icon: TrendingUp,   color: "text-[#00E676]", label: "Better"  },
  worse:   { Icon: TrendingDown, color: "text-[#FF3D00]", label: "Worse"   },
  neutral: { Icon: Minus,        color: "text-[#A1A1AA]", label: "In-line" },
};

function normalizeImpact(impact) {
  if (!impact && impact !== 0) return "low";
  const i = String(impact).toLowerCase().trim();
  if (i === "high"   || i === "3" || i === "red")    return "high";
  if (i === "medium" || i === "2" || i === "orange") return "medium";
  return "low";
}

function outcomeKey(outcome) {
  if (!outcome) return null;
  const o = String(outcome).toLowerCase();
  if (o.includes("better") || o.includes("positive") || o === "good") return "better";
  if (o.includes("worse")  || o.includes("negative") || o === "bad")  return "worse";
  return "neutral";
}

function ImpactBadge({ impact }) {
  const key = normalizeImpact(impact);
  const cfg = IMPACT_CONFIG[key];
  return (
    <span className={`flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-full border whitespace-nowrap ${cfg.bg} ${cfg.color}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
}

function OutcomePill({ outcome }) {
  const key = outcomeKey(outcome);
  if (!key) return <span className="text-[#52525B] text-xs">Pending</span>;
  const { Icon, color, label } = OUTCOME_CONFIG[key];
  return (
    <span className={`flex items-center gap-1 text-xs font-medium ${color}`}>
      <Icon className="w-3.5 h-3.5" /> {label}
    </span>
  );
}

function TimeDisplay({ dateStr }) {
  if (!dateStr) return <span className="text-[#52525B]">—</span>;
  try {
    const d = new Date(dateStr);
    if (isNaN(d)) return <span className="text-xs text-[#A1A1AA] font-mono">{dateStr}</span>;
    return (
      <span className="text-xs text-[#A1A1AA] font-mono">
        {d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
      </span>
    );
  } catch {
    return <span className="text-xs text-[#A1A1AA]">{dateStr}</span>;
  }
}

function DataCell({ value, highlight }) {
  const empty = value === null || value === undefined || value === "" || value === "N/A";
  return (
    <span className={`font-mono text-sm font-semibold ${empty ? "text-[#52525B]" : highlight ? "text-[#00E676]" : "text-white"}`}>
      {empty ? "—" : value}
    </span>
  );
}

function getWeekRange(offset = 0) {
  const today = new Date();
  const monday = new Date(today);
  monday.setDate(today.getDate() - today.getDay() + 1 + offset * 7);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  const fmt = d => d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  return `${fmt(monday)} – ${fmt(sunday)}`;
}

export default function LivePnL() {
  const [events, setEvents]           = useState([]);
  const [loading, setLoading]         = useState(true);
  const [refreshing, setRefreshing]   = useState(false);
  const [currency, setCurrency]       = useState("All");
  const [search, setSearch]           = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);
  const [error, setError]             = useState(null);
  const [thisWeek, setThisWeek]       = useState(false);
  const [weekOffset, setWeekOffset]   = useState(0);

  const fetchCalendar = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setError(null);
    try {
      const params = thisWeek
        ? { this_week: true, week_offset: weekOffset }
        : {};
      const res = await axios.get(`${API}/news/forex`, { params });
      if (res.data.success) {
        setEvents(res.data.events || []);
        setLastUpdated(new Date());
      }
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load calendar.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [thisWeek, weekOffset]);

  useEffect(() => { fetchCalendar(); }, [fetchCalendar]);

  // Filtering
  const displayed = events.filter(ev => {
    const matchCcy = currency === "All" || ev.currency === currency;
    const matchSearch = !search ||
      ev.name?.toLowerCase().includes(search.toLowerCase()) ||
      ev.currency?.toLowerCase().includes(search.toLowerCase());
    return matchCcy && matchSearch;
  });

  const highCount   = events.filter(e => normalizeImpact(e.impact) === "high").length;
  const mediumCount = events.filter(e => normalizeImpact(e.impact) === "medium").length;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <Navbar />

      <div className="pt-24 pb-20 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">

          {/* Header */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
            <div>
              <h1 className="text-2xl sm:text-4xl font-bold mb-2 flex items-center gap-2 sm:gap-3">
                <Calendar className="w-9 h-9 text-[#007AFF]" />
                Forex Economic Calendar
              </h1>
              <p className="text-[#A1A1AA] flex items-center gap-2">
                <Clock className="w-4 h-4" />
                {lastUpdated
                  ? `Updated ${lastUpdated.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} — ${events.length} events`
                  : "Loading from TradingView…"}
              </p>
            </div>
            <Button
              onClick={() => fetchCalendar(true)}
              disabled={refreshing || loading}
              variant="outline"
              className="border-[#27272A] hover:border-[#007AFF] text-white"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>

          {/* Today / This Week toggle + week navigation */}
          <div className="flex flex-wrap items-center gap-3 mb-6">
            <button
              onClick={() => { setThisWeek(false); setWeekOffset(0); }}
              className={`px-5 py-2 rounded-lg text-sm font-medium border transition-colors ${
                !thisWeek
                  ? "bg-[#007AFF] border-[#007AFF] text-white"
                  : "border-[#27272A] text-[#A1A1AA] hover:border-[#007AFF] hover:text-white"
              }`}
            >
              Today
            </button>
            <button
              onClick={() => { setThisWeek(true); setWeekOffset(0); }}
              className={`px-5 py-2 rounded-lg text-sm font-medium border transition-colors ${
                thisWeek
                  ? "bg-[#7C3AED] border-[#7C3AED] text-white"
                  : "border-[#27272A] text-[#A1A1AA] hover:border-[#7C3AED] hover:text-white"
              }`}
            >
              This Week
            </button>

            {/* Week navigator — only shown in week mode */}
            {thisWeek && (
              <div className="flex items-center gap-2 ml-2 border border-[#27272A] rounded-lg px-2 py-1">
                <button
                  onClick={() => setWeekOffset(w => w - 1)}
                  className="p-1 hover:text-[#7C3AED] transition-colors"
                  title="Previous week"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-xs text-[#A1A1AA] font-medium min-w-[80px] sm:min-w-[130px] text-center">
                  {weekOffset === 0 ? "This week" : getWeekRange(weekOffset)}
                </span>
                <button
                  onClick={() => setWeekOffset(w => w + 1)}
                  className="p-1 hover:text-[#7C3AED] transition-colors"
                  title="Next week"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>



          {/* Currency filter + search */}
          <div className="flex flex-col md:flex-row gap-3 mb-6">
            <div className="flex flex-wrap gap-2">
              {CURRENCIES.map(c => (
                <button
                  key={c}
                  onClick={() => setCurrency(c)}
                  className={`px-3 py-1.5 rounded-full text-xs font-mono font-semibold border transition-all ${
                    currency === c
                      ? "bg-[#007AFF] border-[#007AFF] text-white"
                      : "border-[#27272A] text-[#A1A1AA] hover:border-[#007AFF] hover:text-white"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
            <div className="relative md:ml-auto md:w-64">
              <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A1A1AA]" />
              <Input
                placeholder="Search event or currency…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="bg-[#1C1C1E] border-[#27272A] text-white pl-10 text-sm"
              />
            </div>
          </div>

          {/* Summary pills */}
          {!loading && events.length > 0 && (
            <div className="flex flex-wrap gap-2 sm:gap-3 mb-6">
              <div className="bg-[#111111] border border-[#27272A] rounded-lg px-3 py-1.5 sm:px-4 sm:py-2 flex items-center gap-2">
                <Globe className="w-4 h-4 text-[#007AFF]" />
                <span className="text-sm text-[#A1A1AA]">Total</span>
                <span className="text-sm font-bold text-white">{events.length}</span>
              </div>
              <div className="bg-[#111111] border border-[#FF3D00]/30 rounded-lg px-4 py-2 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-[#FF3D00]" />
                <span className="text-sm text-[#A1A1AA]">High Impact</span>
                <span className="text-sm font-bold text-[#FF3D00]">{highCount}</span>
              </div>
              <div className="bg-[#111111] border border-[#FFC107]/30 rounded-lg px-4 py-2 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-[#FFC107]" />
                <span className="text-sm text-[#A1A1AA]">Medium</span>
                <span className="text-sm font-bold text-[#FFC107]">{mediumCount}</span>
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="bg-[#FF3D00]/10 border border-[#FF3D00]/30 rounded-xl p-8 text-center mb-6">
              <AlertTriangle className="w-10 h-10 text-[#FF3D00] mx-auto mb-3" />
              <p className="text-[#FF3D00] font-semibold mb-2">{error}</p>
              <p className="text-[#A1A1AA] text-sm">
                Unable to load the ForexFactory calendar. Please try refreshing.
              </p>
              <button
                onClick={() => fetchCalendar(true)}
                className="mt-4 px-4 py-2 rounded-lg text-sm border border-[#FF3D00]/40 text-[#FF3D00] hover:bg-[#FF3D00]/10 transition-colors"
              >
                Try Again
              </button>
            </div>
          )}


          {/* Skeleton */}
          {loading && (
            <div className="space-y-3">
              {Array.from({ length: 10 }).map((_, i) => (
                <div key={i} className="bg-[#111111] border border-[#27272A] rounded-xl p-4 animate-pulse">
                  <div className="flex gap-4">
                    <div className="h-4 w-12 bg-[#1C1C1E] rounded" />
                    <div className="h-4 w-16 bg-[#1C1C1E] rounded" />
                    <div className="h-4 flex-1 bg-[#1C1C1E] rounded" />
                    <div className="h-4 w-20 bg-[#1C1C1E] rounded" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Empty */}
          {!loading && !error && displayed.length === 0 && (
            <div className="text-center py-20">
              <Calendar className="w-14 h-14 text-[#27272A] mx-auto mb-4" />
              <p className="text-[#52525B] text-lg">No events found for the selected filters.</p>
            </div>
          )}

          {/* Calendar table */}
          {!loading && !error && displayed.length > 0 && (
            <div className="bg-[#111111] border border-[#27272A] rounded-xl overflow-hidden">
              {/* Table header (desktop) */}
              <div className="hidden md:grid grid-cols-[72px_64px_1fr_96px_96px_96px_96px_96px] gap-2 px-4 py-2.5 border-b border-[#1C1C1E] bg-[#0D0D0D]">
                {["Time", "Ccy", "Event", "Impact", "Actual", "Forecast", "Previous", "Outcome"].map(h => (
                  <span key={h} className="text-xs font-medium text-[#52525B] uppercase tracking-wide">{h}</span>
                ))}
              </div>

              {displayed.map((ev, i) => {
                const impactKey = normalizeImpact(ev.impact);
                const borderColor =
                  impactKey === "high"   ? "border-l-[#FF3D00]" :
                  impactKey === "medium" ? "border-l-[#FFC107]" :
                                          "border-l-transparent";

                return (
                  <div
                    key={ev.event_id || i}
                    className={`grid grid-cols-1 md:grid-cols-[72px_64px_1fr_96px_96px_96px_96px_96px] gap-1 md:gap-2 px-4 py-3 items-center transition-colors hover:bg-[#161616] border-l-2 ${borderColor} ${
                      i < displayed.length - 1 ? "border-b border-[#1C1C1E]" : ""
                    }`}
                  >
                    {/* Time */}
                    <TimeDisplay dateStr={ev.date} />

                    {/* Currency (desktop) */}
                    <span className="hidden md:inline text-xs font-mono font-bold text-[#007AFF] bg-[#007AFF]/10 px-2 py-0.5 rounded w-fit">
                      {ev.currency || "—"}
                    </span>

                    {/* Event name + mobile meta */}
                    <div>
                      <div className="md:hidden flex flex-wrap items-center gap-2 mb-1">
                        <span className="text-xs font-mono font-bold text-[#007AFF] bg-[#007AFF]/10 px-2 py-0.5 rounded">
                          {ev.currency || "—"}
                        </span>
                        <ImpactBadge impact={ev.impact} />
                      </div>
                      <p className="text-sm font-medium text-white leading-tight">{ev.name || "—"}</p>
                      {ev.projection && ev.projection !== "N/A" && (
                        <p className="text-xs text-[#52525B] mt-0.5">Projection: {ev.projection}</p>
                      )}
                    </div>

                    {/* Impact (desktop) */}
                    <div className="hidden md:block">
                      <ImpactBadge impact={ev.impact} />
                    </div>

                    {/* Actual */}
                    <DataCell value={ev.actual} highlight />

                    {/* Forecast */}
                    <DataCell value={ev.forecast} />

                    {/* Previous */}
                    <DataCell value={ev.previous} />

                    {/* Outcome */}
                    <OutcomePill outcome={ev.outcome} />
                  </div>
                );
              })}
            </div>
          )}

        </div>
      </div>

      <Footer />
    </div>
  );
}
